/**
 * Password Generator Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { IGeneratorTool } from '@/interfaces/ITool';

function pickRandomChar(alphabet: string, randomByte: number): string {
  return alphabet[randomByte % alphabet.length]!;
}

function getRandomBytes(count: number): Uint8Array {
  if (!globalThis.crypto?.getRandomValues) {
    throw ToolError.execution('Secure random generator not available');
  }
  const bytes = new Uint8Array(count);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

export const PasswordGeneratorTool: IGeneratorTool = {
  id: 'password-generator',
  name: 'Password Generator',
  description: 'Generate secure random passwords',
  version: '1.0.0',
  type: 'generator',
  category: 'Generators',
  keywords: ['password', 'generator', 'security', 'random'],

  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'count',
      type: 'number',
      label: 'Count',
      defaultValue: 5,
      validation: { min: 1, max: 100, step: 1 },
      description: 'Number of passwords to generate',
    },
    {
      name: 'length',
      type: 'number',
      label: 'Length',
      defaultValue: 16,
      validation: { min: 4, max: 128, step: 1 },
      description: 'Password length',
    },
    {
      name: 'lowercase',
      type: 'boolean',
      label: 'Lowercase',
      defaultValue: true,
      description: 'Include lowercase letters (a-z)',
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase',
      defaultValue: true,
      description: 'Include uppercase letters (A-Z)',
    },
    {
      name: 'digits',
      type: 'boolean',
      label: 'Digits',
      defaultValue: true,
      description: 'Include digits (0-9)',
    },
    {
      name: 'symbols',
      type: 'boolean',
      label: 'Symbols',
      defaultValue: false,
      description: 'Include symbols',
    },
    {
      name: 'excludeAmbiguous',
      type: 'boolean',
      label: 'Exclude Ambiguous',
      defaultValue: true,
      description: 'Exclude ambiguous characters (O/0, l/1, etc.)',
    },
  ],

  async generate(config: Record<string, unknown>): Promise<string> {
    const count = typeof config.count === 'number' ? (config.count as number) : 5;
    const length = typeof config.length === 'number' ? (config.length as number) : 16;

    const includeLower = config.lowercase !== false;
    const includeUpper = config.uppercase !== false;
    const includeDigits = config.digits !== false;
    const includeSymbols = config.symbols === true;
    const excludeAmbiguous = config.excludeAmbiguous !== false;

    if (count < 1) throw ToolError.validation('Count must be at least 1');
    if (length < 4) throw ToolError.validation('Length must be at least 4');

    const lower = excludeAmbiguous ? 'abcdefghijkmnopqrstuvwxyz' : 'abcdefghijklmnopqrstuvwxyz';
    const upper = excludeAmbiguous ? 'ABCDEFGHJKMNPQRSTUVWXYZ' : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const digits = excludeAmbiguous ? '23456789' : '0123456789';
    const symbols = '!@#$%^&*()-_=+[]{};:,.?';

    const pools: string[] = [];
    if (includeLower) pools.push(lower);
    if (includeUpper) pools.push(upper);
    if (includeDigits) pools.push(digits);
    if (includeSymbols) pools.push(symbols);
    if (pools.length === 0) throw ToolError.validation('Select at least one character set');

    const allAlphabet = pools.join('');
    const requiredPools = pools;

    const passwords: string[] = [];
    const bytesNeededPerPassword = length * 2 + requiredPools.length;
    const bytes = getRandomBytes(count * bytesNeededPerPassword);

    let offset = 0;
    for (let i = 0; i < count; i++) {
      const chars: string[] = [];

      // Ensure at least one from each selected pool.
      for (const pool of requiredPools) {
        chars.push(pickRandomChar(pool, bytes[offset++]!));
      }

      // Fill the rest.
      while (chars.length < length) {
        chars.push(pickRandomChar(allAlphabet, bytes[offset++]!));
      }

      // Shuffle.
      for (let j = chars.length - 1; j > 0; j--) {
        const k = bytes[offset++]! % (j + 1);
        [chars[j], chars[k]] = [chars[k]!, chars[j]!];
      }

      passwords.push(chars.join(''));
    }

    return passwords.join('\n');
  },
};
